import React, { useState } from "react";
import { Link } from "react-router-dom";
import { ShieldAlert, ArrowLeft, KeyRound, Copy } from "lucide-react";
import { api, formatError } from "@/lib/api";
import { toast } from "sonner";

export default function AdminForgotPassword() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/auth/admin/forgot-password", { email });
      setResult(data);
    } catch (err) { toast.error(formatError(err)); } finally { setLoading(false); }
  };

  const copy = (t) => { navigator.clipboard?.writeText(t); toast.success("Link copied"); };

  return (
    <div className="min-h-screen flex items-center justify-center px-6" data-testid="admin-forgot-page">
      <div className="w-full max-w-md">
        <Link to="/" className="block text-center font-display font-bold tracking-tight text-lg text-ink-900">
          NIRAJ <span className="text-gold">BHAARDWAJ</span>
        </Link>
        <div className="mt-3 text-center overline">Password Recovery</div>
        <div className="glass rounded-2xl p-8 mt-6 space-y-5">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-full bg-gold/10 border border-gold/30 flex items-center justify-center">
              <KeyRound className="w-5 h-5 text-gold" />
            </div>
            <div>
              <h1 className="font-display text-xl font-bold tracking-tight text-ink-900">Reset admin password</h1>
              <p className="text-xs text-ink-600">Enter your admin email to receive a reset link.</p>
            </div>
          </div>

          {!result ? (
            <form onSubmit={submit} className="space-y-4" data-testid="admin-forgot-form">
              <label className="block">
                <div className="overline mb-2 !text-ink-600">Admin email</div>
                <input required type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                  className="w-full rounded-lg bg-cream-50 border border-ink-900/15 px-4 py-3 outline-none focus:border-gold text-ink-900"
                  data-testid="admin-forgot-email" />
              </label>
              <button type="submit" disabled={loading} className="btn-neon w-full disabled:opacity-60" data-testid="admin-forgot-submit">
                {loading ? "Sending..." : "Send reset link"}
              </button>
            </form>
          ) : (
            <div className="space-y-4" data-testid="admin-forgot-result">
              <div className="rounded-lg bg-gold/10 border border-gold/30 p-4 text-sm text-ink-800">
                {result.message}
              </div>
              {result.reset_link && (
                <div className="space-y-2">
                  <div className="text-xs text-ink-500">{result.dev_notice}</div>
                  <div className="flex items-center gap-2 rounded-lg bg-cream-50 border border-ink-900/15 px-3 py-2">
                    <ShieldAlert className="w-4 h-4 text-gold shrink-0" />
                    <code className="flex-1 text-xs text-ink-800 truncate" data-testid="admin-forgot-link-preview">{result.reset_link}</code>
                    <button type="button" onClick={() => copy(result.reset_link)} className="text-ink-600 hover:text-gold" data-testid="admin-forgot-copy"><Copy className="w-4 h-4" /></button>
                  </div>
                  <Link to={result.reset_link.replace(window.location.origin, "")} className="btn-neon w-full text-sm !py-2" data-testid="admin-forgot-open">
                    Open reset link
                  </Link>
                </div>
              )}
              <button type="button" onClick={() => { setResult(null); setEmail(""); }} className="btn-ghost w-full text-sm !py-2">Send another</button>
            </div>
          )}
          <Link to="/admin/login" className="flex items-center justify-center gap-1.5 text-xs text-ink-600 hover:text-gold" data-testid="admin-forgot-back">
            <ArrowLeft className="w-3.5 h-3.5" /> Back to admin sign in
          </Link>
        </div>
      </div>
    </div>
  );
}
